/**
 * Tenant-scoped browser storage helpers (audit F-LEAK-023..027, F-LEAK-051).
 */

import type { User } from '../types';
import type { B2BBuyer } from '../types/b2b';
import { getActiveCompanyId } from './activeCompany';

export const SESSION_END_EVENT = 'invfu:session-end';

export function resolveTenantAdminId(
  user: Pick<User, 'id' | 'role' | 'admin_id'> | null,
  buyer: Pick<B2BBuyer, 'admin_id'> | null,
): string | null {
  if (user) {
    // Admins can switch between the agency's companies, so caches must be
    // namespaced by the active company rather than by the login identity.
    if (user.role === 'admin') return getActiveCompanyId() || user.id;
    if (user.role === 'super_admin') return user.id;
    return user.admin_id || user.id;
  }
  if (buyer) return buyer.admin_id;
  return null;
}

/** Session scope for namespacing caches (user id or b2b buyer id). */
export function resolveSessionScopeId(
  user: Pick<User, 'id' | 'role' | 'admin_id'> | null,
  buyer: Pick<B2BBuyer, 'id'> | null,
): string | null {
  if (user) return user.id;
  if (buyer) return buyer.id;
  return null;
}

export function readSessionScopeIdFromStorage(): string | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr) as User;
      return user.id;
    }
    const buyerStr = localStorage.getItem('b2b_buyer');
    if (buyerStr) {
      const buyer = JSON.parse(buyerStr) as B2BBuyer;
      return buyer.id;
    }
  } catch {
    // ignore
  }
  return null;
}

export function namespacedStorageKey(baseKey: string, scopeId: string | null): string {
  return scopeId ? `${baseKey}:${scopeId}` : baseKey;
}

export function migrateLegacyStorageKey(legacyKey: string, namespacedKey: string): void {
  if (typeof localStorage === 'undefined') return;
  if (legacyKey === namespacedKey) return;
  try {
    const legacy = localStorage.getItem(legacyKey);
    if (legacy && !localStorage.getItem(namespacedKey)) {
      localStorage.setItem(namespacedKey, legacy);
    }
    localStorage.removeItem(legacyKey);
  } catch {
    // ignore
  }
}

export function dispatchSessionEnd(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SESSION_END_EVENT));
}
